import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseBannerCarouselOptions {
  total: number
  autoplayInterval?: number
  firstSlideInterval?: number
  autoplay?: boolean
  onSwipe?: (info: {
    direction: 'left' | 'right'
    fromIndex: number
    toIndex: number
  }) => void
  onAutoplayCycle?: (info: { totalBanners: number; cycleCount: number }) => void
  reducedMotion?: boolean
  transitionDuration?: number
  hoveringRef?: React.RefObject<boolean>
  /** Per-slide flag; `true` means the consumer drives advancement. */
  skipAutoplayPerSlide?: readonly boolean[]
}

export interface UseBannerCarouselReturn {
  currentIndex: number
  goTo: (index: number) => void
  next: () => void
  prev: () => void
  pause: () => void
  resume: () => void
  renderIndex: number
  isTransitioning: boolean
  trackRef: (el: HTMLDivElement | null) => void
  dragHandlers: {
    onPointerDown: (e: React.PointerEvent) => void
  }
  dragOffset: number
  isDragging: boolean
  notifyManualInteraction: () => void
  lastAdvanceSourceRef: React.RefObject<'auto' | 'manual'>
  activeInterval: number
  isAutoplayActive: boolean
}
export function useBannerCarousel(
  options: UseBannerCarouselOptions,
): UseBannerCarouselReturn {
  const {
    total,
    autoplayInterval = 5000,
    firstSlideInterval = 7000,
    autoplay = true,
    onSwipe,
    onAutoplayCycle,
    reducedMotion = false,
    transitionDuration = 400,
    hoveringRef,
    skipAutoplayPerSlide,
  } = options

  // renderIndex includes clones: [cloneLast, 0, 1, ..., N-1, cloneFirst]
  const [renderIndex, setRenderIndex] = useState(1)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const currentIndex = renderIndex - 1

  useEffect(() => {
    if (total <= 0) return
    setRenderIndex(prev => {
      if (prev < 1 || prev > total) {
        setIsTransitioning(false)
        return 1
      }
      return prev
    })
  }, [total])

  const trackElRef = useRef<HTMLDivElement | null>(null)
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pausedRef = useRef(false)
  const manualInteractionRef = useRef(false)
  const [isAutoplayActive, setIsAutoplayActive] = useState(false)
  const cycleCountRef = useRef(0)
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const lastAdvanceSourceRef = useRef<'auto' | 'manual'>('auto')
  const firstSlideUsedRef = useRef(false)

  const trackRef = useCallback((el: HTMLDivElement | null) => {
    trackElRef.current = el
  }, [])

  useEffect(() => {
    if (reducedMotion) return
    const el = trackElRef.current
    if (!el) return

    const handleTransitionEnd = () => {
      setIsTransitioning(false)

      setRenderIndex(prev => {
        if (prev <= 0) {
          // Was at clone of last slide → jump to real last slide
          return total
        }
        if (prev >= total + 1) {
          // Was at clone of first slide → jump to real first slide
          return 1
        }
        return prev
      })
    }

    el.addEventListener('transitionend', handleTransitionEnd)
    return () => el.removeEventListener('transitionend', handleTransitionEnd)
  }, [total, reducedMotion])

  const clearTransitionTimeout = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
    }
  }, [])

  const goToRender = useCallback(
    (nextRenderIndex: number) => {
      if (total <= 1) return
      if (reducedMotion) {
        setIsTransitioning(true)
        let nextIndex = nextRenderIndex
        if (nextIndex <= 0) nextIndex = total
        if (nextIndex >= total + 1) nextIndex = 1
        setRenderIndex(nextIndex)
        clearTransitionTimeout()
        transitionTimeoutRef.current = setTimeout(() => {
          setIsTransitioning(false)
        }, transitionDuration)
        return
      }
      setIsTransitioning(true)
      setRenderIndex(nextRenderIndex)
    },
    [total, reducedMotion, transitionDuration, clearTransitionTimeout],
  )

  const next = useCallback(() => {
    if (isTransitioning) return
    goToRender(renderIndex + 1)
  }, [isTransitioning, renderIndex, goToRender])

  const prev = useCallback(() => {
    if (isTransitioning) return
    goToRender(renderIndex - 1)
  }, [isTransitioning, renderIndex, goToRender])

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return
      const nextRenderIndex = index + 1
      if (nextRenderIndex === renderIndex) return
      goToRender(nextRenderIndex) // +1 for clone offset
    },
    [isTransitioning, renderIndex, goToRender],
  )

  const clearAutoplayTimer = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current)
      autoplayTimerRef.current = null
    }
  }, [])

  const startAutoplayTimer = useCallback(() => {
    clearAutoplayTimer()
    setIsAutoplayActive(false)
    if (
      !autoplay ||
      total <= 1 ||
      pausedRef.current ||
      manualInteractionRef.current ||
      skipAutoplayPerSlide?.[currentIndex] === true
    )
      return

    const useFirstInterval = currentIndex === 0 && !firstSlideUsedRef.current
    const interval = useFirstInterval ? firstSlideInterval : autoplayInterval
    setIsAutoplayActive(true)
    autoplayTimerRef.current = setTimeout(() => {
      if (!pausedRef.current && !manualInteractionRef.current) {
        if (useFirstInterval) firstSlideUsedRef.current = true
        lastAdvanceSourceRef.current = 'auto'
        if (currentIndex === total - 1) {
          cycleCountRef.current++
          onAutoplayCycle?.({
            totalBanners: total,
            cycleCount: cycleCountRef.current,
          })
        }
        next()
      }
    }, interval)
  }, [
    autoplay,
    total,
    currentIndex,
    firstSlideInterval,
    autoplayInterval,
    clearAutoplayTimer,
    next,
    onAutoplayCycle,
    skipAutoplayPerSlide,
  ])

  useEffect(() => {
    if (!isTransitioning) {
      startAutoplayTimer()
    }
    return clearAutoplayTimer
  }, [isTransitioning, startAutoplayTimer, clearAutoplayTimer])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearAutoplayTimer()
        setIsAutoplayActive(false)
      } else if (!pausedRef.current) {
        startAutoplayTimer()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [clearAutoplayTimer, startAutoplayTimer])

  const pause = useCallback(() => {
    pausedRef.current = true
    clearAutoplayTimer()
  }, [clearAutoplayTimer])

  const resume = useCallback(() => {
    if (hoveringRef?.current) return
    pausedRef.current = false
    if (manualInteractionRef.current) return
    startAutoplayTimer()
  }, [startAutoplayTimer])

  useEffect(() => {
    return () => {
      clearTransitionTimeout()
    }
  }, [clearTransitionTimeout])

  const notifyManualInteraction = useCallback(() => {
    lastAdvanceSourceRef.current = 'manual'
    manualInteractionRef.current = true
    clearAutoplayTimer()
    setIsAutoplayActive(false)
  }, [clearAutoplayTimer])

  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStateRef = useRef<{
    startX: number
    startY: number
    currentX: number
    locked: boolean // locked to horizontal
    pointerId: number
    target: HTMLElement | null
  } | null>(null)

  // Use refs to avoid stale closures in document-level listeners
  const finalizeDragRef = useRef<() => void>(() => {})
  const resumeRef = useRef(resume)
  resumeRef.current = resume
  const dragCleanupRef = useRef<(() => void) | null>(null)

  // Clean up document listeners if component unmounts mid-drag
  useEffect(() => () => dragCleanupRef.current?.(), [])

  const finalizeDrag = useCallback(() => {
    const state = dragStateRef.current
    if (!state) return

    const dx = state.currentX - state.startX
    const el = trackElRef.current
    const slideWidth = el ? el.clientWidth / (reducedMotion ? 1 : total + 2) : 0
    const threshold = slideWidth ? slideWidth * 0.2 : 50

    if (state.locked) {
      lastAdvanceSourceRef.current = 'manual'
      if (Math.abs(dx) > threshold) {
        const from = Math.max(0, Math.min(renderIndex - 1, total - 1))
        if (dx < 0) {
          onSwipe?.({
            direction: 'left',
            fromIndex: from,
            toIndex: (from + 1) % total,
          })
          next()
        } else {
          onSwipe?.({
            direction: 'right',
            fromIndex: from,
            toIndex: (from - 1 + total) % total,
          })
          prev()
        }
      }
    }

    dragStateRef.current = null
    setDragOffset(0)
    setIsDragging(false)
    resume()
  }, [next, prev, resume, renderIndex, total, onSwipe, reducedMotion])

  finalizeDragRef.current = finalizeDrag

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (total <= 1) return
      // Only primary button
      if (e.button !== 0) return

      const pointerId = e.pointerId

      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        currentX: e.clientX,
        locked: false,
        pointerId,
        target: e.currentTarget as HTMLElement,
      }

      pause()

      // Use document-level listeners instead of pointer capture so that
      // we reliably receive pointermove/pointerup even when the cursor
      // leaves the track element (fast swipes, trackpad flings), while
      // still allowing native click events to reach the Link elements.

      // Tracks the click interceptor registered on horizontal drag start.
      // setTimeout(0) in onDocPointerUp removes it if no click fires.
      let pendingClickInterceptor: ((ce: MouseEvent) => void) | null = null

      const onDocPointerMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return
        const state = dragStateRef.current
        if (!state) return

        const dx = ev.clientX - state.startX
        const dy = ev.clientY - state.startY

        if (!state.locked) {
          if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
            if (Math.abs(dx) > Math.abs(dy)) {
              state.locked = true
              setIsDragging(true)
              pendingClickInterceptor = ce => {
                ce.stopPropagation()
                ce.preventDefault()
                pendingClickInterceptor = null
              }
              state.target?.addEventListener('click', pendingClickInterceptor, {
                capture: true,
                once: true,
              })
            } else {
              // Vertical scroll → cancel drag
              removeDocListeners()
              dragStateRef.current = null
              setDragOffset(0)
              resumeRef.current()
              return
            }
          } else {
            return
          }
        }

        ev.preventDefault()
        state.currentX = ev.clientX
        setDragOffset(dx)
      }

      const onDocPointerUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return
        const target = dragStateRef.current?.target
        const interceptor = pendingClickInterceptor
        pendingClickInterceptor = null
        removeDocListeners()
        finalizeDragRef.current()
        // Defer so click fires first; removeEventListener is a no-op if already consumed.
        if (interceptor) {
          setTimeout(() => {
            target?.removeEventListener('click', interceptor, { capture: true })
          }, 0)
        }
      }

      const onDocPointerCancel = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return
        cleanup()
        dragStateRef.current = null
        setDragOffset(0)
        setIsDragging(false)
        resumeRef.current()
      }

      const removeDocListeners = () => {
        document.removeEventListener('pointermove', onDocPointerMove)
        document.removeEventListener('pointerup', onDocPointerUp)
        document.removeEventListener('pointercancel', onDocPointerCancel)
        dragCleanupRef.current = null
      }

      // Unmount mid-drag: remove stale interceptor + doc listeners.
      const cleanup = () => {
        if (pendingClickInterceptor) {
          dragStateRef.current?.target?.removeEventListener(
            'click',
            pendingClickInterceptor,
            { capture: true },
          )
          pendingClickInterceptor = null
        }
        removeDocListeners()
      }

      document.addEventListener('pointermove', onDocPointerMove)
      document.addEventListener('pointerup', onDocPointerUp)
      document.addEventListener('pointercancel', onDocPointerCancel)
      dragCleanupRef.current = cleanup
    },
    [total, pause],
  )

  const dragHandlers = {
    onPointerDown,
  }

  const clampedIndex = Math.max(0, Math.min(currentIndex, total - 1))
  const activeInterval =
    clampedIndex === 0 && !firstSlideUsedRef.current
      ? firstSlideInterval
      : autoplayInterval

  return {
    currentIndex: clampedIndex,
    goTo,
    next,
    prev,
    pause,
    resume,
    renderIndex,
    isTransitioning,
    trackRef,
    dragHandlers,
    dragOffset,
    isDragging,
    notifyManualInteraction,
    lastAdvanceSourceRef,
    activeInterval,
    isAutoplayActive,
  }
}
